import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { EducatorsService } from '@/services/educators'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card'
import { toast } from 'sonner'

const formSchema = z.object({
  full_name: z.string().min(3, 'Nome completo é obrigatório'),
  email: z.string().email('Email inválido'),
  whatsapp: z.string().min(10, 'WhatsApp é obrigatório'),
  city: z.string().min(2, 'Cidade é obrigatória'),
  desired_position: z.string().min(2, 'Cargo desejado é obrigatório'),
  target_grade_level: z.string().min(2, 'Série/Etapa é obrigatória'),
  experience_years: z.string().min(1, 'Anos de experiência é obrigatório'),
  last_position: z.string().min(2, 'Último cargo é obrigatório'),
  immediate_availability: z.enum(['yes', 'no'], {
    required_error: 'Selecione a disponibilidade',
    invalid_type_error: 'Selecione a disponibilidade',
  }),
})

interface EducatorFormProps {
  onSuccess: () => void
}

export function EducatorForm({ onSuccess }: EducatorFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      full_name: '',
      email: '',
      whatsapp: '',
      city: '',
      desired_position: '',
      target_grade_level: '',
      experience_years: '',
      last_position: '',
      immediate_availability: 'no',
    },
  })

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsSubmitting(true)

    try {
      // Step 1: Upload Resume (Removed per new requirement)

      // Step 2: Prepare Serializable Payload (No File Objects, No Resume fields)
      // We omit 'status' to allow the database to apply the default value 'new'
      // This ensures compliance with the check constraint and avoids invalid status values
      const payload = {
        full_name: values.full_name,
        email: values.email,
        whatsapp: values.whatsapp,
        city: values.city,
        desired_position: values.desired_position,
        target_grade_level: values.target_grade_level,
        experience_years: parseInt(values.experience_years, 10),
        last_position: values.last_position,
        immediate_availability: values.immediate_availability === 'yes',
      }

      // Step 3: Save to Database
      await EducatorsService.create(payload)

      toast.success('Cadastro realizado com sucesso!')
      onSuccess()
    } catch (error: any) {
      console.error('Form submission failed:', error)
      const msg = error.message || 'Erro ao enviar cadastro. Tente novamente.'
      toast.error(msg)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader className="bg-white border-b border-slate-100 pb-6">
        <CardTitle className="text-xl text-brand-navy">
          Seu Perfil Profissional
        </CardTitle>
        <CardDescription>
          Preencha com atenção para aumentarmos suas chances de match.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-6">
              <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-2">
                Dados Pessoais e Contato
              </h3>

              <FormField
                control={form.control}
                name="full_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome Completo</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Seu nome completo"
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="seu@email.com"
                          type="email"
                          {...field}
                          value={field.value ?? ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="whatsapp"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>WhatsApp</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="(11) 99999-9999"
                          {...field}
                          value={field.value ?? ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cidade de Residência</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Ex: São Paulo"
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="space-y-6 pt-4">
              <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-2">
                Experiência Profissional
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="desired_position"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cargo Desejado</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Ex: Professor de Matemática"
                          {...field}
                          value={field.value ?? ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="target_grade_level"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Série / Faixa Etária Foco</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Ex: Ensino Médio"
                          {...field}
                          value={field.value ?? ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="experience_years"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Anos de Experiência</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="Ex: 5"
                          {...field}
                          value={field.value ?? ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="last_position"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Último Cargo / Escola</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Ex: Professor no Colégio X"
                          {...field}
                          value={field.value ?? ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="immediate_availability"
                render={({ field }) => (
                  <FormItem className="space-y-3">
                    <FormLabel>Disponibilidade Imediata?</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        value={field.value ?? ''}
                        className="flex flex-col space-y-1 sm:flex-row sm:space-y-0 sm:space-x-4"
                      >
                        <FormItem className="flex items-center space-x-3 space-y-0">
                          <FormControl>
                            <RadioGroupItem value="yes" />
                          </FormControl>
                          <FormLabel className="font-normal">
                            Sim, posso começar imediatamente
                          </FormLabel>
                        </FormItem>
                        <FormItem className="flex items-center space-x-3 space-y-0">
                          <FormControl>
                            <RadioGroupItem value="no" />
                          </FormControl>
                          <FormLabel className="font-normal">
                            Não, preciso de aviso prévio
                          </FormLabel>
                        </FormItem>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Button
              type="submit"
              size="lg"
              className="w-full bg-brand-emerald hover:bg-emerald-600 text-lg h-12 mt-6"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Enviando Cadastro...' : 'Cadastrar Meu Perfil'}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}
